import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MANAGEMENT_PAGES, nextManagement, ManagementKey } from "../lib/managementPages";
import styles from "../styles/managementmobilenav.module.css";

/**
 * ManagementMobileNav — ported from claude.ai/design `nav-mobile.js`.
 * Shared mobile nav for the 4 management systems. Tabs + drawer "4가지의 관리"
 * items call `onSelect(key)` so the central hub swaps content in place
 * (no route change). `mbBg`/`mbFrame` frame classes are exported via the CSS module.
 */

const ChevDown = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><polyline points="6 9 12 15 18 9" /></svg>
);
const ContactIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" /><path d="M21 16v2a4 4 0 0 1-4 4h-5" /></svg>
);
const MenuIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></svg>
);
const ArrowRight = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"><line x1="4" y1="12" x2="20" y2="12" /><polyline points="14 6 20 12 14 18" /></svg>
);

const ManagementMobileNav: React.FC<{
    /** 관리 허브에서만 전달 — 없으면(전역 크롬) 관리 탭 스트립·진행바를 숨긴다. */
    current?: ManagementKey;
    onSelect: (key: ManagementKey) => void;
    /** 헤드셋 버튼 — 하단 '빠른상담'과 동일한 QuickConsultSheet를 연다. */
    onOpenQuick?: () => void;
    /** 전역 사이트 크롬용 — 구 헤더(fixed)+페이지 스페이서 레이아웃에 맞춰 fixed로 렌더. */
    fixed?: boolean;
    /** fixed 바를 아래로 미는 오프셋(px) — /winter 상단 고정 띠(38px) 아래에 둘 때 */
    topOffset?: number;
}> = ({ current, onSelect, onOpenQuick, fixed, topOffset }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // 처음엔 전부 닫힘(헤더+칼럼.dc.html) — 관리 허브에서만 4가지 관리를 펼쳐 둔다.
    const [acc, setAcc] = useState({ intro: false, mgmt: !!current, mentor: false, notice: false, consult: false });
    const isCommunity = useLocation().pathname.toLowerCase().startsWith("/community");
    const tabsRef = useRef<HTMLElement>(null);

    // Scroll: hide header on scroll-down / show on scroll-up + tab progress (this page 0→100%)
    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const THRESHOLD = 8;
        const TOP_GUARD = 32;
        const onScroll = () => {
            const y = window.scrollY;
            const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
            // 진행바는 4가지 관리 누적(각 25%): 현재 관리 인덱스 + 페이지 스크롤 진행도. (탭은 허브에서만 렌더)
            if (current && tabsRef.current) {
                const pageProg = Math.min(1, Math.max(0, y / max));
                const idx = Math.max(0, MANAGEMENT_PAGES.findIndex((p) => p.key === current));
                const pct = ((idx + pageProg) / MANAGEMENT_PAGES.length) * 100;
                tabsRef.current.style.setProperty("--lm-progress", pct + "%");
            }
            if (Math.abs(y - lastY) >= THRESHOLD) {
                if (y < TOP_GUARD) setCollapsed(false);
                else if (y > lastY) setCollapsed(true);
                else setCollapsed(false);
                lastY = y;
            }
            ticking = false;
        };
        onScroll();
        const handler = () => {
            if (!ticking) { requestAnimationFrame(onScroll); ticking = true; }
        };
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, [current]);

    // 한 번에 하나만 열림 — 다른 드롭다운은 모두 닫는다 (dc.html과 동일, M-6).
    const toggleAcc = (k: keyof typeof acc) =>
        setAcc((s) => ({ intro: false, mgmt: false, mentor: false, notice: false, consult: false, [k]: !s[k] }));
    const selectAndClose = (key: ManagementKey) => {
        // 같은 관리 재선택이면 최상단으로 스크롤(M-1).
        if (key === current) window.scrollTo({ top: 0, behavior: "smooth" });
        else onSelect(key);
        setDrawerOpen(false);
    };

    return (
        <>
            <div className={`${styles.topbar} ${fixed ? styles.topbarFixed : ""} ${collapsed ? styles.isCollapsed : ""}`} style={fixed && topOffset ? { top: topOffset, ...(collapsed ? { transform: `translateY(-${64 + topOffset}px)` } : {}) } : undefined}>
                <header className={styles.bar}>
                    <Link className={styles.logo} to="/" aria-label="수능선배 홈으로">
                        <img src="/img/management/sunbae-logo.svg" alt="수능선배" />
                    </Link>
                    <div className={styles.actions}>
                        <button
                            className={styles.iconBtn}
                            type="button"
                            aria-label="빠른 상담"
                            aria-haspopup="dialog"
                            onClick={() => onOpenQuick?.()}
                        >
                            <ContactIcon />
                        </button>
                        <button
                            className={`${styles.iconBtn} ${styles.burger}`}
                            type="button"
                            aria-label="메뉴 열기"
                            aria-expanded={drawerOpen}
                            onClick={() => setDrawerOpen(true)}
                        >
                            <MenuIcon />
                        </button>
                    </div>
                </header>
                {current && (
                    <nav ref={tabsRef} className={styles.tabs} aria-label="관리 시스템">
                        {MANAGEMENT_PAGES.map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                className={styles.tab}
                                aria-current={p.key === current ? "page" : undefined}
                                onClick={() => (p.key === current ? window.scrollTo({ top: 0, behavior: "smooth" }) : onSelect(p.key))}
                            >
                                {p.label}
                            </button>
                        ))}
                    </nav>
                )}
            </div>

            {/* Drawer */}
            <nav className={`${styles.drawer} ${drawerOpen ? styles.isOpen : ""}`} aria-label="주 메뉴" aria-hidden={!drawerOpen}>
                <div className={styles.drawerHead}>
                    <Link className={styles.drawerLogo} to="/" aria-label="수능선배 홈으로" onClick={() => setDrawerOpen(false)}>
                        <img src="/img/management/sunbae-logo.svg" alt="수능선배" />
                    </Link>
                    <button className={styles.drawerClose} type="button" aria-label="메뉴 닫기" onClick={() => setDrawerOpen(false)} />
                </div>
                <div className={styles.acc}>
                    <div className={`${styles.accItem} ${acc.intro ? styles.isOpen : ""}`}>
                        <div className={styles.accRow} onClick={() => toggleAcc("intro")}>
                            <span>수능선배 소개</span>
                            <span className={styles.accChev}><ChevDown /></span>
                        </div>
                        <div className={styles.accPanel}>
                            <Link to="/ceo" onClick={() => setDrawerOpen(false)}>대표 소개</Link>
                            <Link to="/mapmain" onClick={() => setDrawerOpen(false)}>지점 시설 및 비용 안내</Link>
                        </div>
                    </div>

                    <div className={styles.accItem}>
                        <div className={styles.accRow}>
                            <Link to="/winter" onClick={() => setDrawerOpen(false)}>윈터스쿨 모집</Link>
                        </div>
                    </div>

                    <div className={styles.accItem}>
                        <div className={styles.accRow}>
                            <Link to="/regular" onClick={() => setDrawerOpen(false)}>대입정규반 모집</Link>
                        </div>
                    </div>

                    <div className={`${styles.accItem} ${acc.mgmt ? styles.isOpen : ""}`}>
                        <div className={styles.accRow} onClick={() => toggleAcc("mgmt")}>
                            <span>4가지 관리</span>
                            <span className={styles.accChev}><ChevDown /></span>
                        </div>
                        <div className={styles.accPanel}>
                            {MANAGEMENT_PAGES.map((p) => (
                                <button
                                    key={p.key}
                                    type="button"
                                    aria-current={p.key === current ? "page" : undefined}
                                    onClick={() => selectAndClose(p.key)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`${styles.accItem} ${acc.mentor ? styles.isOpen : ""}`}>
                        <div className={styles.accRow} onClick={() => toggleAcc("mentor")}>
                            <span>담임멘토</span>
                            <span className={styles.accChev}><ChevDown /></span>
                        </div>
                        <div className={styles.accPanel}>
                            <Link to="/teachers" onClick={() => setDrawerOpen(false)}>담임 멘토 리스트</Link>
                        </div>
                    </div>

                    <div className={styles.accItem}>
                        <div className={styles.accRow}>
                            <Link to="/community" aria-current={isCommunity ? "page" : undefined} onClick={() => setDrawerOpen(false)}>커뮤니티</Link>
                        </div>
                    </div>

                    <div className={`${styles.accItem} ${acc.notice ? styles.isOpen : ""}`}>
                        <div className={styles.accRow} onClick={() => toggleAcc("notice")}>
                            <span>공지사항</span>
                            <span className={styles.accChev}><ChevDown /></span>
                        </div>
                        <div className={styles.accPanel}>
                            <Link to="/notification" onClick={() => setDrawerOpen(false)}>공지</Link>
                            <Link to="/faq" onClick={() => setDrawerOpen(false)}>자주 묻는 질문</Link>
                            <Link to="/review" onClick={() => setDrawerOpen(false)}>합격생 이야기</Link>
                            <Link to="/mealmenu" onClick={() => setDrawerOpen(false)}>식단표</Link>
                            <Link to="/investpage" onClick={() => setDrawerOpen(false)}>투자 문의</Link>
                        </div>
                    </div>

                    <div className={`${styles.accItem} ${acc.consult ? styles.isOpen : ""}`}>
                        <div className={styles.accRow} onClick={() => toggleAcc("consult")}>
                            <span>상담/대기 신청하기</span>
                            <span className={styles.accChev}><ChevDown /></span>
                        </div>
                        <div className={styles.accPanel}>
                            <Link to="/register" onClick={() => setDrawerOpen(false)}>상담 신청</Link>
                            <Link to="/registerwaiting2026" onClick={() => setDrawerOpen(false)}>대기 신청</Link>
                            <button type="button" onClick={() => { setDrawerOpen(false); onOpenQuick?.(); }}>빠른 상담</button>
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
};

/** 푸터 위 '다음 관리 시스템 → {다음}' CTA. 클릭 시 허브의 active를 다음 관리로 전환. */
export const ManagementNextMobileCta: React.FC<{
    current: ManagementKey;
    onSelect: (key: ManagementKey) => void;
}> = ({ current, onSelect }) => {
    const next = nextManagement(current);
    return (
        <section className={styles.next} aria-label="다음 관리">
            <div className={styles.nextLabel}>다음 관리 시스템</div>
            <button type="button" className={styles.nextBtn} onClick={() => onSelect(next.key)}>
                <span className={styles.nextText}>
                    <span className={styles.nextEyebrow}>NEXT</span>
                    <span className={styles.nextTitle}>{next.label}</span>
                </span>
                <span className={styles.nextArrow} aria-hidden="true"><ArrowRight /></span>
            </button>
        </section>
    );
};

export default ManagementMobileNav;
